import { memo, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpCircle, CheckCircle2, CircleDashed, HelpCircle, Server, TrendingUp } from 'lucide-react';
import type { PartnerPack, ReportView } from '../../types/partners';
import { cn } from '../../utils/cn';
import { clusterUpdatePath, versionTriad, type TriadCell, type TriadState } from '../../utils/partnerVersions';

interface VersionTriadProps {
  pack: PartnerPack;
  /** The report to line up against; the pack's latest report when omitted. */
  report?: ReportView | null;
  /** The viewer can open Modpack Configs, so "Update the cluster" can link there. */
  canOpenConfigs: boolean;
  className?: string;
}

const STATE: Record<TriadState, { Icon: ComponentType<{ className?: string }>; cls: string }> = {
  match: { Icon: CheckCircle2, cls: 'text-green-400' },
  behind: { Icon: AlertTriangle, cls: 'text-amber-300' },
  ahead: { Icon: ArrowUpCircle, cls: 'text-accent-300' },
  differs: { Icon: AlertTriangle, cls: 'text-amber-300' },
  missing: { Icon: CircleDashed, cls: 'text-glass-500' },
  unknown: { Icon: HelpCircle, cls: 'text-glass-500' },
};

const Cell = ({ title, cell, children }: { title: string; cell: TriadCell; children?: ReactNode }) => {
  const { Icon, cls } = STATE[cell.state];
  return (
    <div className="min-w-0 space-y-1" data-state={cell.state}>
      <p className="eyebrow">{title}</p>
      <p className="font-mono text-sm text-white break-all">{cell.value ?? '—'}</p>
      <p className={cn('text-xs flex items-start gap-1', cls)}>
        <Icon className="w-3.5 h-3.5 mt-px shrink-0" />
        <span>{cell.label}</span>
      </p>
      {children}
    </div>
  );
};

/**
 * Partner reported · On CurseForge · We run, side by side for one pack, so
 * staff can see at a glance whether our servers are behind what the partner
 * shipped, and jump to the cluster to update it.
 */
export const VersionTriad = memo(({ pack, report, canOpenConfigs, className }: VersionTriadProps) => {
  const against = report === undefined ? pack.latest_report : report;
  const triad = versionTriad({
    reported: against?.version ?? null,
    curseforgeName: against?.curseforge?.display_name ?? pack.cf?.latest_display_name ?? null,
    curseforgeWatched: pack.apogee_id !== null,
    curseforgeListed: !!against?.curseforge,
    curseforgeError: pack.cf?.last_error ?? null,
    weRun: pack.we_run,
  });
  const clusters = triad.weRun.clusters;

  return (
    <div className={cn('grid gap-3 sm:grid-cols-3', className)} aria-label={`Versions of ${pack.name}`}>
      <Cell title="Partner reported" cell={triad.reported} />
      <Cell title="On CurseForge" cell={triad.curseforge}>
        {pack.cf?.last_error && pack.cf.latest_display_name && <p className="text-[11px] text-glass-500">The last check failed: {pack.cf.last_error}</p>}
      </Cell>
      <Cell title="We run" cell={triad.weRun}>
        {clusters.length > 1 && (
          <ul className="space-y-0.5">
            {clusters.map((cluster, i) => (
              <li key={`${cluster.cluster_parent ?? 'x'}-${i}`} className="text-[11px] text-glass-400 flex items-center gap-1 min-w-0">
                <Server className="w-3 h-3 shrink-0" />
                <span className="truncate">{cluster.cluster_name ?? cluster.head_address ?? 'Cluster'}</span>
                <span className={cn('font-mono', STATE[cluster.state].cls)}>{cluster.version ?? '—'}</span>
              </li>
            ))}
          </ul>
        )}
        {canOpenConfigs &&
          triad.behindClusters
            .filter((cluster) => cluster.head_address)
            .map((cluster, i) => (
              <Link
                key={`${cluster.head_address}-${i}`}
                to={clusterUpdatePath(cluster.head_address as string)}
                className="link text-xs inline-flex items-center gap-1"
                title={`Bump ${cluster.cluster_name ?? cluster.head_address} to ${triad.reported.value} in Modpack Configs`}
              >
                <TrendingUp className="w-3 h-3" />
                Update the cluster{triad.behindClusters.length > 1 ? ` (${cluster.cluster_name ?? cluster.head_address})` : ''}
              </Link>
            ))}
      </Cell>
    </div>
  );
});
VersionTriad.displayName = 'VersionTriad';
